import {debug} from "./debugging";

type DomReaderCallback = (node: Node) => void

export const phoneNumberRegex = /(?:(?:\+?([1-9]|[0-9][0-9]|[0-9][0-9][0-9])\s*(?:[.\-–—]\s*)?)?(?:\(\s*([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9])\s*\)|([0-9][1-9]|[0-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9]))\s*(?:[.\-–—]\s*)?)?([2-9]1[02-9]|[2-9][02-9]1|[2-9][02-9]{2})\s*(?:[.\-–—]\s*)?([0-9]{4})(?:\s*(?:#|x\.?|ext\.?|extension)\s*(\d+))?/g

export const nodeContainsPhoneNumber = (node: Node): boolean => {
    if ((node as HTMLElement).innerHTML?.includes('tel:')) {
        return true;
    }

    if ((node as HTMLAnchorElement).href?.includes('tel:')) {
        return true;
    }

    if (node.textContent?.trim() === '') {
        return false;
    }

    if (! node.textContent?.match(/\d/)) {
        return false;
    }

    // if (! node.textContent?.match(phoneNumberRegex)) {
    //     return false;
    // }

    return true;
}

export const getNodesWithSwapPotential = (node: Node): Node[] => {
    const nodes: Node[] = [];

    // -----------------------------
    // #1: Process the current node
    // -----------------------------

    if (node.parentElement instanceof HTMLScriptElement) {
        return nodes;
    }

    if (node.parentElement?.closest('.no-swap')) {
        return nodes;
    }

    if (node.nodeType === Node.TEXT_NODE) {
            return [node];
    }

    if (node instanceof HTMLAnchorElement) {
        nodes.push(node);
    }

    // -----------------------------
    // #2: Process child nodes
    // -----------------------------

    if (!nodeContainsPhoneNumber(node)) {
        return nodes;
    }

    // Check if this node's combined text might contain a phone number split across children
    // e.g., <p><strong>800</strong>-<strong>888</strong>-<strong>0001</strong></p>
    const textContent = node.textContent || '';

    // Test if combined text contains a phone number
    phoneNumberRegex.lastIndex = 0;
    const hasPhoneNumberPattern = phoneNumberRegex.test(textContent);

    // If the combined text contains a phone number and we have multiple children with digits,
    // check if any individual child contains a complete phone number
    if (hasPhoneNumberPattern && node.childNodes.length > 1) {
        const childrenWithDigits = Array.from(node.childNodes).filter(
            child => child.textContent?.match(/\d/)
        );

        if (childrenWithDigits.length > 1) {
            // Check if any child contains a complete phone number
            const hasChildWithCompleteNumber = childrenWithDigits.some(child => {
                phoneNumberRegex.lastIndex = 0;
                return phoneNumberRegex.test(child.textContent || '');
            });

            // Only return parent if no child has a complete phone number (i.e., number is truly split)
            if (!hasChildWithCompleteNumber) {
                return [node];
            }
        }
    }

    for (let i= 0; i < node.childNodes.length; i++) {
        const swappableChildNodes = getNodesWithSwapPotential(node.childNodes.item(i));

        nodes.push(...swappableChildNodes);
    }

    return nodes.filter(n => n.nodeValue !== null || (n as HTMLAnchorElement).href !== null);
}

const getUnprocessedNodeFilter = (cache: WeakSet<WeakKey>) => {
    return function(node: Node): boolean {
        // debug('Swapper.observeNode', node);

        // #1: Check if node is already in cache
        if (cache.has(node)) {
            // debug('Swapper.observeNode: node already in cache', node);
            return false;
        }

        // #2: Add node to cache
        // debug('Swapper.observeNode: observed node first time', node);
        cache.add(node);
        return true;
    };
}

const logMutationText = (node: Node): Node => {
    debug('[mutation] Detected text node:', node.textContent);
    return node;
}

export const startWatching = (callback: DomReaderCallback): VoidFunction => {
    const cache = new WeakSet<WeakKey>();

    const nodeFilter = getUnprocessedNodeFilter(cache);

    const observer = new MutationObserver((mutations: MutationRecord[]) => {
        debug("Observed mutation");

        mutations.filter((mutation) => mutation.addedNodes.length !== 0)
            .forEach((mutation) => {
                Array.from(mutation.addedNodes)
                    .map((node) => {
                        getNodesWithSwapPotential(node)
                            .filter(nodeFilter)
                            .forEach(callback);
                    });
            });
    });

    function onDomContentLoaded() {
        // Perform walk on domcontentloaded (todo: DRY)
        debug('DOMContentLoaded: performing walk');

        const nodes = getNodesWithSwapPotential(document.body);

        debug('DOMContentLoaded: walk discovered', nodes.length, 'nodes');

        nodes.filter(nodeFilter).forEach(callback);
    }

    document.addEventListener('DOMContentLoaded', onDomContentLoaded);

    debug('Document Ready State:', document.readyState)
    if (document.readyState === 'complete' || document.readyState === 'interactive') {
        getNodesWithSwapPotential(document.body).filter(nodeFilter).forEach(callback);
    }

    debug('observing changes');
    observer.observe(document, {
        childList: true,
        subtree: true,
        characterData: true,
    });

    // stop the watcher.
    return () => {
        document.removeEventListener('DOMContentLoaded', onDomContentLoaded)
        observer.disconnect();
    };
}